<script context="module" lang="ts">
  import { MetafyPartnerEnum } from '$lib/types/api/graphql';

  import { writable } from 'svelte/store';

  export let isVisible = writable(false);

  type CheckoutDrawerParams = {
    plan: MetafyPartnerEnum;
    isYearly?: boolean;
    onUnlocked?: (() => Promise<void>) | (() => void) | null;
  };
  const selDefault: CheckoutDrawerParams = {
    plan: MetafyPartnerEnum.Pro,
    isYearly: true,
    onUnlocked: null
  };
  const sel = writable(selDefault);

  export function openCheckoutDrawer(params: CheckoutDrawerParams) {
    const defaultParams = { ...selDefault, ...params };
    sel.set(defaultParams);
    isVisible.set(true);
  }
</script>

<script lang="ts">
  import type { App } from '$lib/types';
  import type {
    Api_Order,
    MetafyPartnerEnum,
    ProductPurchaseInput,
    PurchaseCreateInput,
    PurchasePreviewInput,
    RecurlyBillingInfoInput
  } from '$lib/types/api/graphql';
  import { page } from '$app/stores';

  import { createEventDispatcher, getContext } from 'svelte';
  import { mutate } from '$lib/apollo';
  import { GET_IRK_SESSION } from '$graphql/queries';
  import { PREVIEW_SUBSCRIPTION_PURCHASE, PURCHASE_SUBSCRIPTION } from '../gql';
  import { purchaseCreateErrorToast, secureFlow } from '../payment/payment';

  import { Toasts } from '$lib/stores';
  import { Drawer } from '$mds/Dialogs';
  import PaymentMethods from '$components/Pro/payment/PaymentMethods.svelte';
  import Button from '$mds/Buttons/Button/Button.svelte';
  import TermsLink from './components/TermsLink.svelte';

  import BadgeCore from '$assets/svgs/badges/core.svg?component';
  import BadgePro from '$assets/svgs/badges/pro.svg?component';
  //import BadgeUltra from '$assets/svgs/badges/ultra.svg?component';

  import { analytics, debounce } from '$lib/utils';
  import { TRACK_PRO_CHECKOUT_CLOSED, TRACK_PRO_CHECKOUT_STARTED, TRACK_PRO_SUB_STARTED } from '$lib/tracking/pro';
  import { trackCheckoutStarted, trackOrderCompleted } from './tracking';
  import { PLANS } from '$routes/pro/_components/plans';
  import Price from '$components/Price.svelte';
  import { openProUnlockedModal } from './ProUnlockedModal.svelte';
  import { goto } from '$app/navigation';
  import FormControl from '$components/FormControl.svelte';
  import Tag from '$mds/Tag';
  import LoadingSpinner from '$mds/LoadingSpinner';
  import { applyTax } from '$routes/checkout/tax';
  import { browser } from '$app/environment';

  $: plan = PLANS[$sel.plan];

  const dispatch = createEventDispatcher();
  const { featureFlags, irkAccount, isPartner, isPro } = getContext<App>('app');

  let paymentDrawer;
  let paymentKind;
  let paymentGateway;
  let isPaymentMethodValid;
  let payWithCredit = true;
  let lockPaymentButton = false;
  let isPaymentProcessing = false;
  let preview: Api_Order | Record<string, any> = {};
  let orderId: Pick<Api_Order, 'id'>;
  let isCompletedCheckout = false;
  let isPreviewPurchaseLoading = false;
  let isYearly = $sel.isYearly;
  let country: string;
  let postalCode: string;

  // New Billing Flow
  let executePayment: (confirmToken?: string, secureToken?: string) => Promise<Record<string, unknown>>;
  let hasPaymentMethodSelected = false;

  $: if (browser) {
    payWithCredit, isYearly, country, postalCode, lockThenPreview({ update: true });
  }

  const PRODUCT_TYPES = {
    [MetafyPartnerEnum.Core]: 'METAFY_CORE',
    [MetafyPartnerEnum.Pro]: 'METAFY_PRO'
    // ultra: 'METAFY_ULTRA'
  };

  const PRICING_OPTION_IDS = {
    monthly: '47e56589-98dc-435e-b06c-78421dcbfc93',
    yearly: 'e0b2b4fb-f829-489f-821b-0bb99506d39f'
  };

  $: PRODUCT = {
    productType: PRODUCT_TYPES[$sel.plan],
    quantity: 1,
    pricingOptionId: isYearly ? PRICING_OPTION_IDS['yearly'] : PRICING_OPTION_IDS['monthly']
  } as ProductPurchaseInput;

  const close = () => {
    paymentDrawer?.closeModal();
  };

  const proUnlocked = () => {
    isCompletedCheckout = true;
    $sel.onUnlocked?.();
    if ($page.url.pathname === '/pro') {
      openProUnlockedModal({ onUnlocked: () => goto(`/@${$irkAccount?.slug}`) });
    } else {
      openProUnlockedModal({});
    }
    dispatch('unlocked');
    close();
  };

  const lockThenPreview = async ({ update = false } = {}) => {
    lockPaymentButton = true;
    await previewPurchase({ update });
  };

  const previewPurchase = debounce(async ({ update = false } = {}) => {
    isPreviewPurchaseLoading = true;
    lockPaymentButton = true;
    try {
      const input: Partial<PurchasePreviewInput> = { products: [PRODUCT], useCredit: payWithCredit };
      const { data } = await mutate({
        mutation: PREVIEW_SUBSCRIPTION_PURCHASE,
        variables: { input }
      });
      preview = (data.purchasePreview?.result?.order || {}) as Api_Order;

      const now = await applyTax(preview, country, postalCode);
      if (now) {
        preview = {
          ...preview,
          taxes: parseFloat(now.taxes),
          total: parseFloat(now.total),
          totalDue: parseFloat(now.total)
        };
      }

      if (!orderId) {
        orderId = preview.id;
      }

      if (!update) {
        trackCheckoutStarted(orderId, preview);
      }
    } catch (error) {
      Toasts.push({
        kind: 'error',
        content: 'Error fetching Partner subscription details, please try again.'
      });
      close();
    } finally {
      isPreviewPurchaseLoading = false;
      lockPaymentButton = false;
    }
  }, 250);

  async function fetchToken() {
    let token = null;
    if (paymentGateway) {
      token = await paymentGateway.fetchToken();
    }
    return token;
  }

  async function getBillingInfo(
    paymentGatewayResponseSecureToken: string | undefined,
    useCreditToggle: boolean
  ): Promise<{ billingInfo: RecurlyBillingInfoInput } | {}> {
    let failed = false;
    if (paymentGatewayResponseSecureToken) {
      // 3ds flow
      // hide the checkout drawer because 3ds modal would be stuck behind it
      paymentDrawer.closeModal();
      let threeDSecureResultToken: { token?: string; message?: string } = {};
      try {
        let token = await fetchToken();
        threeDSecureResultToken = await secureFlow(paymentGatewayResponseSecureToken);
        return { billingInfo: { token, secureToken: threeDSecureResultToken.token } };
      } catch (error) {
        failed = true;
      }
      isVisible.set(true);
      payWithCredit = useCreditToggle;
    } else {
      // non-3ds flow
      // recurly saves the previous card info, so if the user selects that, we don't need to do anything
      if (paymentKind === 'PREVIOUS_CARD') return {};

      try {
        let token = await fetchToken();
        return { billingInfo: { token } };
      } catch (error) {
        failed = true;
      }

      if (failed) {
        Toasts.push({
          kind: 'error',
          content: 'Error in Payment Authorisation, please try again.'
        });
      }
    }
    return {};
  }

  const paymentSuccess = (order: Api_Order) => {
    analytics.track({ name: TRACK_PRO_SUB_STARTED });
    trackOrderCompleted(orderId, order);
    proUnlocked();
  };

  const handlePayment = async (confirmToken?: string, secureToken?: string) => {
    // store the eligibility here, because it will change after the
    // payment goes through and the profile updates
    try {
      lockPaymentButton = true;
      isPaymentProcessing = true;

      let billingInfo: Record<string, unknown> = {};
      if (preview?.totalDue > 0) {
        billingInfo = await executePayment(confirmToken, secureToken);
      }

      const input: Partial<PurchaseCreateInput> = { products: [PRODUCT], ...billingInfo, useCredit: payWithCredit };
      const { data } = await mutate({
        mutation: PURCHASE_SUBSCRIPTION,
        variables: { input },
        refetchQueries: [GET_IRK_SESSION],
        awaitRefetchQueries: true
      });
      const { result, errors } = data.purchaseCreate;
      const needs3dSecure = result?.paymentGatewayResponse?.code === 'three_d_secure_action_required';
      if (needs3dSecure) {
        // resubmit payment, but this time with the secure token process
        await handlePayment(
          billingInfo?.billingInfo?.paymentGatewayReferences?.[0]?.token,
          result?.paymentGatewayResponse?.secureToken
        );
        return;
      } else if (errors?.length || !result || result.order.state === 'VOID') {
        purchaseCreateErrorToast({ errors, result });
        isPaymentProcessing = false;
        lockPaymentButton = false;
      } else {
        // SUCCESS
        paymentSuccess(result.order);
      }
    } catch (error) {
      Toasts.push({
        kind: 'error',
        content: 'Error creating Partner subscription, please try again.'
      });
      isPaymentProcessing = false;
      lockPaymentButton = false;
    }
  };

  $: isSwitchAvailable =
    $isPro && $irkAccount?.metafyPartnerSubscription?.product?.billingFrequency !== (isYearly ? 'year' : 'month');
</script>

<Drawer
  bind:drawer={paymentDrawer}
  bind:isOpen={$isVisible}
  title="Checkout"
  backIcon
  class="border-0 {$sel.plan === MetafyPartnerEnum.Pro ? 'bg-plan-pro' : 'bg-plan-core'}"
  on:open={() => {
    analytics.track({ name: TRACK_PRO_CHECKOUT_STARTED, props: { from: $page.url.pathname } });
    lockThenPreview();
  }}
  on:closed={() => {
    if (!isCompletedCheckout) {
      analytics.track({ name: TRACK_PRO_CHECKOUT_CLOSED });
    }
  }}
>
  <section class="flex min-h-[calc(100%-2px)] flex-col justify-between gap-4">
    <header class="flex flex-col gap-y-4 text-center">
      {#if $sel.plan === MetafyPartnerEnum.Core}
        <BadgeCore class="mx-auto size-12 shrink-0" />
      {:else if $sel.plan === MetafyPartnerEnum.Pro}
        <BadgePro class="mx-auto size-12 shrink-0" />
        <!-- {:else}
        <BadgeUltra class="mx-auto size-12 shrink-0" /> -->
      {/if}

      <h2 class="text-display-sm text-ondark-primary text-balance">
        Metafy {plan.title}
      </h2>
      <p class="mx-10 mt-2 text-center">{plan.description}</p>

      {#if $sel.plan === MetafyPartnerEnum.Core}
        <div class="my-3 flex items-center justify-center gap-2">
          <span class="flex flex-col">
            <span class="flex gap-2">
              <span class="text-display-sm text-ondark-primary relative transition-colors">
                ${isYearly ? plan.yearly : plan.monthly}
              </span>
            </span>

            <span class="text-body-3xs">per month</span>
          </span>
          <span class="text-body-md text-ondark-subtle mt-2">+</span>

          <span class="flex flex-col">
            <span class="text-ondark-primary text-display-sm">{plan.percentage}%</span>
            <span class="text-body-3xs">of your earnings</span>
          </span>
        </div>
      {/if}
    </header>

    {#if $sel.plan === MetafyPartnerEnum.Pro}
      <div class="flex justify-center">
        <label
          for="toggle-pricing-period-drawer"
          class="border-ondark-subtle relative flex cursor-pointer items-center gap-3 rounded-md border px-3.5 py-3"
        >
          <span class="text-body-xs text-ondark-primary">Monthly</span>
          <FormControl id="toggle-pricing-period-drawer" class="isolate" type="toggle" bind:checked={isYearly} />
          <span class="text-body-xs text-ondark-primary">Yearly</span>
          <Tag variant="positive" size="md">Save 33%</Tag>
        </label>
      </div>
    {/if}

    {#if $sel.plan === $irkAccount?.metafyPartnerType && !isSwitchAvailable}
      <div class="my-auto text-center">
        <h3 class="text-display-lg text-ondark-primary">You're already a Partner!</h3>
      </div>
    {:else if preview?.total === undefined}
      <div class="spinner large animate-pop-in-slow mb-auto mt-3 h-[200px] w-full" />
    {:else if preview?.total > 0}
      <div class="mb-auto mt-3 flex flex-col gap-4">
        <h3 class="text-body-sm text-ondark-subtle">Payment Options</h3>
        {#if $isVisible}
          <PaymentMethods
            bind:isPaymentMethodValid
            bind:paymentGateway
            bind:paymentKind
            bind:payWithCredit
            bind:executePayment
            bind:hasPaymentMethodSelected
            total={preview?.totalDue}
            allowPayWithCredit
            paymentsEnabled={['PREVIOUS_CARD', 'NEW_CARD']}
            on:details={({ detail }) => {
              country = detail.country;
              postalCode = detail.postalCode;
            }}
          />
        {/if}
      </div>
    {/if}
  </section>

  <svelte:fragment slot="actions">
    <footer class="border-ondark-subtle text-ondark-secondary col-span-2 border-t pt-2">
      {#if $sel.plan === MetafyPartnerEnum.Pro && preview?.taxes > 0}
        <div class="mb-2 flex justify-between">
          <p class="text-ondark-subtle">Tax</p>
          <p class="text-ondark-primary font-medium">${preview?.taxes}</p>
        </div>
      {/if}
      <div class="mb-2 flex items-center justify-between">
        <p class="text-ondark-subtle">Amount due</p>

        {#if $sel.plan === MetafyPartnerEnum.Core}
          <p class="text-action-ondark-base text-body-lg">$0.00 USD + 8% of all earnings</p>
        {:else if isPreviewPurchaseLoading}
          <div class="flex w-28 justify-center">
            <LoadingSpinner />
          </div>
        {:else if $sel.plan === MetafyPartnerEnum.Pro}
          <Price
            price={preview?.total ?? 0}
            class="text-body-lg"
            primaryColor="text-action-ondark-base"
            secondaryColor="text-action-ondark-base"
            showCurrencyName
          />
        {/if}
      </div>
    </footer>

    {#if $sel.plan === $irkAccount?.metafyPartnerType && !isSwitchAvailable}
      <Button variant="secondary" size="lg" width="full" class="col-span-2" on:click={proUnlocked}>Oh, Sweet!</Button>
    {:else}
      <Button
        variant="pro"
        size="lg"
        width="full"
        class="col-span-2"
        on:click={() => {
          handlePayment();
        }}
        disabled={!hasPaymentMethodSelected && preview.totalDue > 0}
        loading={lockPaymentButton || isPaymentProcessing}
      >
        {#if !$isPartner}
          Get started
        {:else if $sel.plan === MetafyPartnerEnum.Pro && isSwitchAvailable}
          Change to {isYearly ? 'Yearly' : 'Monthly'} Billing
        {:else}
          Change to <span class="capitalize">{$sel.plan.toLocaleLowerCase()}</span>
        {/if}
      </Button>
    {/if}

    <TermsLink metafyType={$sel.plan} />
  </svelte:fragment>
</Drawer>
